import { algoliasearch } from 'algoliasearch';
import { liteClient as createLiteClient } from 'algoliasearch/lite';
import type { LegacySearchMethodProps, SearchResponse } from 'algoliasearch/lite';
import {
  createBrowserLocalStorageCache,
  createFallbackableCache,
  createMemoryCache,
} from '@algolia/client-common';

// Environment variables
const ALGOLIA_APP_ID = import.meta.env.VITE_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_KEY;
const ALGOLIA_WRITE_API_KEY = import.meta.env.ALGOLIA_WRITE_API_KEY;

// Check if credentials are available
const hasCredentials = !!(ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY);

if (!hasCredentials) {
  console.warn('⚠️ Missing Algolia credentials. Algolia search features will not work.');
}

/**
 * How long identical Algolia search responses stay cached in localStorage,
 * in seconds. Identical repeat queries (backspacing to a previous term,
 * revisiting a page within the TTL) are then served locally instead of
 * counted as billable requests. Aligned with the server-side shelf/CDN
 * cache windows rather than the 5-minute React Query staleTime so hit
 * counts never go stale for long.
 */
const RESPONSES_CACHE_TTL_SECONDS = 120;

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * v4-style requests ({ indexName, query, params }) that the lite client's
 * search() accepts at runtime. The published LegacySearchQuery type omits the
 * root-level `query`, so callers cast their request array to this type.
 */
export type LegacySearchRequests = Array<LegacySearchMethodProps[number] & { query?: string }>;

/**
 * Response shape for hit searches. The lite client types search() results as a
 * union that includes facet-value responses, which have no hits/nbHits.
 */
export type LegacySearchResponses = { results: SearchResponse[] };

/**
 * Full Algolia client for admin operations
 * Use this for server-side operations, index configuration, and data imports
 * DO NOT use in frontend code (exposes admin API key)
 */
export const algoliaAdminClient = typeof window === 'undefined' && ALGOLIA_WRITE_API_KEY && hasCredentials
  ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_WRITE_API_KEY)
  : null;

/**
 * Lightweight Algolia client for frontend searches
 * Smaller bundle size, optimized for search operations only
 * Use this in React components for search UI
 * Returns null if credentials are not available
 *
 * This is the app's single shared lite client (InstantSearch, search
 * services, and autocomplete all import it) so the persistent response
 * cache dedupes identical queries across every surface. The v5 browser
 * default responsesCache is memory-only and dies on reload; the
 * localStorage-backed cache below survives reloads and shares hits
 * between tabs within the TTL.
 */
export const algoliaSearchClient = hasCredentials
  ? createLiteClient(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY, {
      responsesCache: createFallbackableCache({
        caches: [
          ...(canUseLocalStorage()
            ? [createBrowserLocalStorageCache({
                key: `algolia-responses-${ALGOLIA_APP_ID}`,
                timeToLive: RESPONSES_CACHE_TTL_SECONDS,
              })]
            : []),
          createMemoryCache(),
        ],
      }),
    })
  : null;

/**
 * Algolia client with analytics tracking
 * Wraps the lite client with click and conversion tracking
 * Automatically manages user tokens for personalization
 * Returns null if credentials are not available
 */
export const createAlgoliaClientWithAnalytics = (userId?: string) => {
  if (!algoliaSearchClient) {
    console.warn('⚠️ Cannot create analytics client: Algolia credentials missing');
    return null;
  }

  // Generate anonymous user token if no user ID provided
  const userToken = userId || `anonymous_${Date.now()}_${Math.random().toString(36).substring(7)}`;

  return {
    ...algoliaSearchClient,
    userToken,

    /**
     * Track click event for analytics
     * Call this when user clicks a search result
     */
    trackClick: (
      indexName: string,
      objectID: string,
      position: number,
      queryID: string
    ) => {
      // Algolia Insights API call will be added here
      console.log('Track click:', { indexName, objectID, position, queryID, userToken });
    },

    /**
     * Track conversion event for analytics
     * Call this when user downloads a sequence
     */
    trackConversion: (
      indexName: string,
      objectID: string,
      queryID?: string
    ) => {
      // Algolia Insights API call will be added here
      console.log('Track conversion:', { indexName, objectID, queryID, userToken });
    },
  };
};

/**
 * Default search client for InstantSearch
 * Can be replaced with analytics-enabled client when user is authenticated
 * Returns null if credentials are not available
 */
export const defaultSearchClient = algoliaSearchClient;

/**
 * Index names
 */
export const ALGOLIA_INDICES = {
  PRIMARY: 'sequences',
  DOWNLOAD_COUNT_DESC: 'sequences_download_count_desc',
  CREATED_AT_DESC: 'sequences_created_at_desc',
  PRICE_ASC: 'sequences_price_asc',
  PRICE_DESC: 'sequences_price_desc',
  FREE_UPVOTES: 'sequences_free_upvotes',
} as const;

export type AlgoliaIndexName = typeof ALGOLIA_INDICES[keyof typeof ALGOLIA_INDICES];
